import React from 'react';
import { useTranslation } from 'react-i18next';
import { 
  Box, 
  Typography, 
  Paper, 
  Card, 
  CardContent, 
  Grid, 
  Button, 
  Chip,
  CircularProgress,
  Alert,
  Divider,
  Avatar
} from '@mui/material';
import { 
  Person as PersonIcon,
  Email as EmailIcon,
  Phone as PhoneIcon,
  Business as BusinessIcon,
  Work as WorkIcon,
  AccessTime as AccessTimeIcon,
  Edit as EditIcon
} from '@mui/icons-material';
import { useQuery } from '@tanstack/react-query';
import axios from 'axios';
import { format } from 'date-fns';
import { useAuth } from '../../context/AuthContext';

const Profile = () => {
  const { t } = useTranslation();
  const { user, token } = useAuth();

  // Fetch current user profile data
  const { 
    data: profileData, 
    isLoading, 
    isError, 
    error 
  } = useQuery({
    queryKey: ['profile', 'me'],
    queryFn: async () => {
      const response = await axios.get(
        `${process.env.REACT_APP_API_BASE_URL}/users/me`,
        {
          headers: { Authorization: `Bearer ${token}` }
        }
      );
      return response.data;
    },
    enabled: !!token,
    retry: 1,
    refetchOnWindowFocus: false
  });

  const formatDate = (dateString) => {
    if (!dateString) return t('profile:no_data');
    try {
      return format(new Date(dateString), 'dd/MM/yyyy HH:mm');
    } catch (error) {
      return t('profile:no_data');
    }
  };

  const getDisplayName = (item) => {
    if (!item) return t('profile:no_data');
    return item.name || item.username || t('profile:no_data');
  };

  if (isLoading) {
    return (
      <Box display="flex" justifyContent="center" alignItems="center" minHeight="400px">
        <Box textAlign="center">
          <CircularProgress size={48} />
          <Typography variant="body1" sx={{ mt: 2 }}>
            {t('profile:loading_profile')}
          </Typography>
        </Box>
      </Box>
    );
  }

  if (isError) {
    return (
      <Box>
        <Alert severity="error" sx={{ mb: 3 }}>
          {t('profile:error_loading')}
          {error?.response?.data?.message && `: ${error.response.data.message}`}
        </Alert>
      </Box>
    );
  }

  const userData = profileData || user;

  return (
    <Box>
      <Typography variant="h4" component="h1" gutterBottom>
        {t('profile:title')}
      </Typography>
      
      <Typography variant="subtitle1" color="text.secondary" paragraph>
        {t('profile:subtitle')}
      </Typography>

      {/* Personal Information Card */}
      <Card sx={{ mb: 4 }}>
        <CardContent>
          <Box display="flex" alignItems="center" mb={3}>
            <Avatar sx={{ mr: 1, bgcolor: 'primary.main' }}>
              <PersonIcon />
            </Avatar>
            <Typography variant="h6">
              {t('profile:personal_information')}
            </Typography>
          </Box>
          
          <Grid container spacing={3}>
            <Grid item xs={12} md={6}>
              <Box display="flex" alignItems="center" mb={1}>
                <PersonIcon sx={{ mr: 1, color: 'text.secondary', fontSize: 20 }} />
                <Typography variant="body2" color="text.secondary">
                  {t('profile:full_name')}
                </Typography>
              </Box>
              <Typography variant="body1" fontWeight="medium">
                {userData?.username || t('profile:no_data')}
              </Typography>
            </Grid>
            
            <Grid item xs={12} md={6}>
              <Box display="flex" alignItems="center" mb={1}>
                <EmailIcon sx={{ mr: 1, color: 'text.secondary', fontSize: 20 }} />
                <Typography variant="body2" color="text.secondary">
                  {t('profile:email')}
                </Typography>
              </Box>
              <Typography variant="body1" fontWeight="medium">
                {userData?.email || t('profile:no_data')}
              </Typography>
            </Grid>

            <Grid item xs={12} md={6}>
              <Box display="flex" alignItems="center" mb={1}>
                <WorkIcon sx={{ mr: 1, color: 'text.secondary', fontSize: 20 }} />
                <Typography variant="body2" color="text.secondary">
                  {t('profile:position')}
                </Typography>
              </Box>
              <Typography variant="body1" fontWeight="medium">
                {userData?.position || t('profile:no_data')}
              </Typography>
            </Grid>

            <Grid item xs={12} md={6}>
              <Box display="flex" alignItems="center" mb={1}>
                <Typography variant="body2" color="text.secondary">
                  {t('profile:account_status')}
                </Typography>
              </Box>
              <Chip 
                label={userData?.is_active ? t('profile:active_account') : t('profile:inactive_account')}
                color={userData?.is_active ? 'success' : 'error'}
                size="small"
              />
            </Grid>
          </Grid>
        </CardContent>
      </Card>

      {/* Organizational Information Card */}
      <Card sx={{ mb: 4 }}>
        <CardContent>
          <Box display="flex" alignItems="center" mb={3}>
            <Avatar sx={{ mr: 1, bgcolor: 'secondary.main' }}>
              <BusinessIcon />
            </Avatar>
            <Typography variant="h6">
              {t('profile:organizational_information')}
            </Typography>
          </Box>
          
          <Grid container spacing={3}>
            <Grid item xs={12} md={6}>
              <Typography variant="body2" color="text.secondary">
                {t('profile:role')}
              </Typography>
              <Typography variant="body1" fontWeight="medium">
                {getDisplayName(userData?.role)}
              </Typography>
            </Grid>
            
            <Grid item xs={12} md={6}>
              <Typography variant="body2" color="text.secondary">
                {t('profile:department')}
              </Typography>
              <Typography variant="body1" fontWeight="medium">
                {getDisplayName(userData?.department)}
              </Typography>
            </Grid>

            <Grid item xs={12} md={6}>
              <Typography variant="body2" color="text.secondary">
                {t('profile:directorate')}
              </Typography>
              <Typography variant="body1" fontWeight="medium">
                {getDisplayName(userData?.directorate)}
              </Typography>
            </Grid>

            <Grid item xs={12} md={6}>
              <Typography variant="body2" color="text.secondary">
                {t('profile:deputy_ministry')}
              </Typography>
              <Typography variant="body1" fontWeight="medium">
                {getDisplayName(userData?.deputyMinistry)}
              </Typography>
            </Grid>

            {userData?.province && (
              <Grid item xs={12} md={6}>
                <Typography variant="body2" color="text.secondary">
                  {t('profile:province')}
                </Typography>
                <Typography variant="body1" fontWeight="medium">
                  {getDisplayName(userData.province)}
                </Typography>
              </Grid>
            )}
          </Grid>
        </CardContent>
      </Card>

      {/* Account Information Card */}
      <Card sx={{ mb: 4 }}>
        <CardContent>
          <Typography variant="h6" gutterBottom>
            {t('profile:account_information')}
          </Typography>
          
          <Grid container spacing={3}>
            <Grid item xs={12} md={6}>
              <Box display="flex" alignItems="center" mb={1}>
                <AccessTimeIcon sx={{ mr: 1, color: 'text.secondary', fontSize: 20 }} />
                <Typography variant="body2" color="text.secondary">
                  {t('profile:last_login')}
                </Typography>
              </Box>
              <Typography variant="body1" fontWeight="medium">
                {formatDate(userData?.last_login)}
              </Typography>
            </Grid>
            
            <Grid item xs={12} md={6}>
              <Box display="flex" alignItems="center" mb={1}>
                <AccessTimeIcon sx={{ mr: 1, color: 'text.secondary', fontSize: 20 }} />
                <Typography variant="body2" color="text.secondary">
                  {t('profile:member_since')}
                </Typography>
              </Box>
              <Typography variant="body1" fontWeight="medium">
                {formatDate(userData?.created_at)}
              </Typography>
            </Grid>
          </Grid>

          <Divider sx={{ my: 3 }} />
          
          <Box display="flex" justifyContent="flex-end">
            <Button 
              variant="contained" 
              startIcon={<EditIcon />}
              disabled
            >
              {t('profile:edit')}
            </Button>
          </Box>
        </CardContent>
      </Card>

      <Paper sx={{ p: 3, mb: 3 }}>
        <Typography variant="body1" color="text.secondary" textAlign="center">
          {t('common:coming_soon')}
        </Typography>
      </Paper>
    </Box>
  );
};

export default Profile; 